/**
 * Widget configuration types
 */

export type WidgetStyle = 'bubble' | 'inline' | 'modal' | 'immersive' | 'overlay' | 'vertical';
export type WidgetPosition = 'bottom-right' | 'bottom-left';

export interface WidgetTheme {
  // === Core Colors ===
  // Preset theme name (overrides individual colors)
  preset?: 'default' | 'green' | 'blue' | 'gold' | 'red' | 'teal' | 'orange' | 'custom';

  // Color mode - auto-detected from accent color luminance if not set
  // 'dark' = dark background with light text (default neon aesthetic)
  // 'light' = light background with dark text (for sites with dark branding)
  mode?: 'dark' | 'light';

  // Custom accent colors (used when preset is 'custom' or to override)
  accentPrimary?: string;    // Primary neon color (e.g., #00f0ff)
  accentSecondary?: string;  // Secondary neon color (e.g., #a855f7)

  // Legacy - maps to accentPrimary
  primaryColor?: string;

  // === Typography ===
  fontFamily?: string;

  // === Layout ===
  borderRadius?: string;
  bubbleSize?: string;
  panelWidth?: string;
  panelHeight?: string;

  // === Background ===
  backgroundImage?: string;      // URL to background image
  backgroundOverlay?: string;    // Overlay color with opacity (e.g., "rgba(10, 10, 15, 0.9)")
  backgroundBlur?: string;       // Blur amount (e.g., "20px")
  backgroundPosition?: string;   // CSS position (e.g., "center", "top")
  backgroundSize?: string;       // CSS size (e.g., "cover", "contain")

  // === Header ===
  headerBackground?: string;     // Color, gradient, or image URL
  headerBorderColor?: string;    // Accent line below header
  headerLogo?: string;           // Logo URL for header
  headerHeight?: string;         // Height (e.g., "60px")

  // === Messages ===
  messageBubbleStyle?: 'rounded' | 'square' | 'minimal';
  messageSpacing?: 'compact' | 'comfortable' | 'spacious';

  // === Effects ===
  glowIntensity?: 'none' | 'subtle' | 'medium' | 'strong';
  animationStyle?: 'none' | 'subtle' | 'dynamic';

  // === Panel ===
  panelBorderColor?: string;
  panelShadow?: 'none' | 'subtle' | 'medium' | 'strong';

  // === Input ===
  inputBackground?: string;
  inputBorderColor?: string;

  // === Interactive Elements ===
  buttonStyle?: 'pill' | 'rounded' | 'square' | 'ghost';
  cardStyle?: 'elevated' | 'flat' | 'outlined' | 'glass';
  linkStyle?: 'underline' | 'color-only' | 'bold';
  actionChipStyle?: 'solid' | 'outlined' | 'ghost';

  // === Micro-interactions ===
  hoverEffect?: 'lift' | 'glow' | 'scale' | 'none';
  clickFeedback?: 'ripple' | 'pulse' | 'none';
  contentTransition?: 'fade' | 'slide' | 'scale' | 'none';
  focusRingStyle?: 'glow' | 'outline' | 'none';

  // === Polish ===
  scrollbarStyle?: 'thin' | 'hidden' | 'auto';
  scrollbarColor?: string;
  selectionColor?: string;
  skeletonStyle?: 'pulse' | 'wave' | 'none';
  dividerStyle?: 'solid' | 'dashed' | 'gradient' | 'glow' | 'none';
}

/**
 * Greeting block structure for immediate component display
 */
export interface GreetingBlock {
  type: string;
  data: Record<string, unknown>;
}

/**
 * Split panel configuration for side-by-side chat + summary view
 */
export interface SplitPanelConfig {
  /** Enable split panel feature */
  enabled: boolean;
  /** Auto-open summary when first item added (default: true) */
  autoOpen?: boolean;
  /** Title for summary panel (default: 'Your List') */
  summaryTitle?: string;
  /** Empty state message */
  emptyMessage?: string;
  /** Initial panel height as percentage of viewport (default: 65) */
  initialHeight?: number;
}

/**
 * API Discovery configuration for observing host application endpoints
 */
export interface ApiDiscoveryConfig {
  enabled: boolean;
  captureResponses?: boolean; // Opt-in for response schema capture
  sameOriginOnly?: boolean; // Default false - captures all origins (EXCLUDED_DOMAINS filters noise)
  excludePatterns?: string[]; // Regex patterns to exclude
  maxEndpoints?: number; // Limit (default 50)
  reportIntervalMs?: number; // How often to report (default 5000)
}

/**
 * Voice interface configuration
 */
export interface VoiceConfig {
  /** Enable voice features (default: true if supported) */
  enabled?: boolean;
  /** Enable voice input via microphone (default: true) */
  inputEnabled?: boolean;
  /** Enable voice output via text-to-speech (default: true) */
  outputEnabled?: boolean;
  /** Auto-speak AI responses (default: false) */
  autoSpeak?: boolean;
  /** Voice language override (default: uses widget language) */
  language?: string;
}

/**
 * Sample question configuration - can be a simple string or an object with visibility rules.
 *
 * Simple string: shown to all users
 * Object: shown based on `onboardingOnly` (only when not authenticated) or
 *         `authenticatedOnly` (only when authenticated)
 */
export type SampleQuestion = string | {
  text: string;
  /** Show only when user is NOT authenticated (e.g., "Create profile", "Sign up") */
  onboardingOnly?: boolean;
  /** Show only when user IS authenticated (e.g., "View my stats", "Check my orders") */
  authenticatedOnly?: boolean;
};

export interface WidgetConfig {
  workspaceId: string;
  style: WidgetStyle;
  position?: WidgetPosition;
  apiUrl?: string;
  theme?: WidgetTheme;
  /** Color mode override from script tag (takes priority over theme.mode) */
  mode?: 'dark' | 'light';
  greeting?: string;
  placeholder?: string;
  sampleQuestions?: SampleQuestion[];

  /**
   * Whether the current user is authenticated in the host application.
   * Used to filter sample questions (hide onboarding questions for logged-in users).
   *
   * If not explicitly set, the widget will auto-detect by checking `authTokenKeys`
   * in localStorage.
   */
  isAuthenticated?: boolean;

  /**
   * localStorage key(s) to check for authentication tokens.
   * If any of these keys contain a value, the user is considered authenticated.
   *
   * Default: ['accessToken', 'authToken', 'token', 'jwt', 'pms_billing_token']
   */
  authTokenKeys?: string | string[];

  /**
   * Component blocks to render immediately when panel opens.
   * Enables showcasing STATS, INTERACTIVE actions, etc. with zero user interaction.
   */
  greetingBlocks?: GreetingBlock[];

  // Branding (from workspace config)
  branding?: WidgetBranding;

  // Feature flags (from workspace config)
  features?: WidgetFeatures;

  // Split panel configuration for chat + summary side-by-side
  splitPanel?: SplitPanelConfig;

  // API Discovery configuration for observing host application endpoints
  apiDiscovery?: ApiDiscoveryConfig;

  // Internationalization
  /** Widget language (e.g., 'en', 'es', 'fr', 'de', 'ja') */
  language?: string;

  // Voice interface configuration
  voice?: VoiceConfig;

  // Currency configuration (from workspace)
  /** ISO 4217 currency code (e.g., GBP, USD, EUR) */
  currencyCode?: string;
  /** Currency symbol (e.g., £, $, €) */
  currencySymbol?: string;

  // Available flows for this workspace (for client-side validation)
  /** List of available flow IDs */
  availableFlows?: string[];

  /**
   * Quick responses - pre-built responses for specific patterns
   * Bypasses AI for faster responses and cost savings
   */
  quickResponses?: QuickResponse[];

  /**
   * Whether the widget panel auto-opens on page load.
   * - true: panel opens immediately (default for immersive/overlay styles)
   * - false: panel stays closed, user must click bubble to open
   * Can be set via data-auto-open attribute or fetched from backend config.
   */
  autoOpen?: boolean;

  /**
   * Widget visibility: "visible" (default) or "hidden"
   * (widget is invisible — no bubble, panel, or DOM rendered).
   * Can be set via data-visibility attribute or fetched from backend config.
   */
  visibility?: 'visible' | 'hidden';

  /**
   * Whether to passively observe and extract page content as visitors browse.
   * Runs independently of visibility — collects in both visible and hidden modes.
   * Can be set via data-page-observation attribute or fetched from backend config.
   */
  pageObservationEnabled?: boolean;

  /** Business vertical/industry for block styling (e.g., "ecommerce", "saas", "restaurant") */
  vertical?: string;

  /** Color palette override for vertical accent colors (e.g., "cyan", "blue", "orange") */
  colorPalette?: string;

  /** Custom surface/background color override (hex, e.g., "#080a14") */
  surfaceColor?: string;

  /** Knowledge base language hint from backend (e.g., "de", "en") — used as language fallback */
  kbLanguage?: string;

  /** Bubble offset from bottom edge in pixels (default 20) */
  marginBottom?: number;
  /** Bubble offset from side edge in pixels (default 20) */
  marginSide?: number;

  /**
   * Push host page content when panel is open (default: true).
   * Vertical mode pushes content left; immersive pushes content up.
   * Set to false to overlay on top of content instead.
   */
  pushContent?: boolean;
}

/**
 * Quick response - pre-built response for specific message patterns
 * Bypasses AI for faster, consistent responses and cost savings
 */
export interface QuickResponse {
  /** Patterns to match (case-insensitive contains match) */
  patterns: string[];
  /** Pre-built response */
  response: {
    /** Optional text content */
    text?: string;
    /** Pre-built blocks to render */
    blocks: MessageBlock[];
  };
}

/**
 * Feature flags from backend
 */
export interface WidgetFeatures {
  feedbackEnabled?: boolean;
  analyticsEnabled?: boolean;
  quickResponsesEnabled?: boolean;
}

/**
 * Branding configuration
 */
export interface WidgetBranding {
  logoUrl?: string;
  logoAlt?: string;
  companyName?: string;
  favicon?: string;
}

/**
 * Workspace configuration returned by backend API
 */
export interface WorkspaceConfig {
  id: string;
  name: string;
  tagline?: string;
  description?: string;
  logoUrl?: string;
  accentColor?: string; // Legacy - use theme.accentPrimary instead
  greeting?: string;
  placeholder?: string;
  sampleQuestions?: SampleQuestion[];
  /** Component blocks to render immediately when panel opens */
  greetingBlocks?: GreetingBlock[];
  theme?: WidgetTheme;
  branding?: WidgetBranding;
  features?: WidgetFeatures;
  /** ISO 4217 currency code (e.g., GBP, USD, EUR) */
  currencyCode?: string;
  /** Currency symbol (e.g., £, $, €) */
  currencySymbol?: string;
  /** Available flow IDs for this workspace (for client-side validation) */
  availableFlows?: string[];
  /**
   * Quick responses - pre-built responses for specific patterns
   * Bypasses AI for faster responses and cost savings
   */
  quickResponses?: QuickResponse[];
  /** Whether the widget auto-opens on page load */
  autoOpen?: boolean;
  /** Widget visibility from backend: "visible" or "hidden" */
  visibility?: 'visible' | 'hidden';
  /** Whether page observation is enabled (collects page content for KB building) */
  pageObservationEnabled?: boolean;
  /** Business vertical/industry (e.g., "ecommerce", "saas", "healthcare") */
  vertical?: string;
  /** Color palette override for vertical accent colors */
  colorPalette?: string;
  /** Custom surface/background color override (hex, e.g., "#080a14") */
  surfaceColor?: string;
  /** AI-generated custom CSS injected into the Shadow DOM (premium feature) */
  customStyleCss?: string;
  /** Knowledge base language code from backend (e.g., "de", "en") */
  kbLanguage?: string;
  /** E-commerce platform for cart integration ("wix", "shopify", or null) */
  ecommercePlatform?: string;
  /** Widget display style from backend: "bubble", "vertical", "immersive", or "inline" */
  widgetStyle?: string;
  /** Widget position from backend: "bottom-right" or "bottom-left" */
  widgetPosition?: string;
  /** Bubble offset from bottom edge in pixels (default 20) */
  widgetMarginBottom?: number;
  /** Bubble offset from side edge in pixels (default 20) */
  widgetMarginSide?: number;
}

export interface OrchestrateRequest {
  workspaceId: string;
  sessionId: string;
  message: string;
  /** Enable speed mode for direct, concise answers with minimal UI blocks */
  speedMode?: boolean;
  /** User's language code for multilingual AI responses (e.g., 'en', 'es', 'fr', 'de', 'ja') */
  language?: string;
  /** When true, query text is excluded from analytics for authenticated visitor privacy */
  visitorAuthenticated?: boolean;
  /** Current page URL the visitor is viewing on the host site */
  currentPageUrl?: string;
}

export interface MessageBlock {
  type: string;
  data: unknown;
}

export interface OrchestrateResponse {
  sessionId: string;
  message: string;
  blocks?: MessageBlock[];
  error?: string;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  blocks?: MessageBlock[];
  timestamp: Date;
}

/**
 * Default configuration values
 */
export const DEFAULT_CONFIG: Partial<WidgetConfig> = {
  style: 'bubble',
  // position intentionally omitted — all usage sites fall back to 'bottom-right' via ?? operator.
  // Omitting here allows applyWorkspaceConfig() to detect when position was NOT set
  // via data-attribute and apply the backend value instead.
  apiUrl: 'https://backend-production-9253.up.railway.app',
  placeholder: 'Type a message...',
};

export const DEFAULT_THEME: WidgetTheme = {
  // primaryColor intentionally omitted — accent color comes from
  // colorPalette, accentPrimary, or CSS defaults (--pms-primary).
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  borderRadius: '12px',
  bubbleSize: '60px',
  panelWidth: '380px',
  panelHeight: '600px',
};

/**
 * Merge user config with defaults
 */
export function mergeConfig(userConfig: Partial<WidgetConfig>): WidgetConfig {
  if (!userConfig.workspaceId) {
    throw new Error('PromptMySite Widget: workspaceId is required');
  }

  return {
    ...DEFAULT_CONFIG,
    ...userConfig,
    workspaceId: userConfig.workspaceId,
    style: userConfig.style ?? 'bubble',
    theme: {
      ...DEFAULT_THEME,
      ...userConfig.theme,
    },
  };
}

/**
 * Parse data attributes from script tag
 */
export function parseDataAttributes(script: HTMLScriptElement): Partial<WidgetConfig> {
  const config: Partial<WidgetConfig> = {};

  const workspaceId = script.dataset['workspace'];
  if (workspaceId) {
    config.workspaceId = workspaceId;
  }

  // Fallback: extract workspace ID from script src URL query param
  // (needed for Shopify ScriptTag which can't set data-* attributes)
  if (!config.workspaceId && script.src) {
    try {
      const url = new URL(script.src, window.location.href);
      const wsParam = url.searchParams.get('workspace');
      if (wsParam) config.workspaceId = wsParam;
    } catch { /* ignore malformed URLs */ }
  }

  const style = script.dataset['style'] as WidgetStyle | undefined;
  if (style && ['bubble', 'vertical', 'inline', 'modal', 'immersive', 'overlay'].includes(style)) {
    config.style = style;
  }

  const position = script.dataset['position'] as WidgetPosition | undefined;
  if (position && ['bottom-right', 'bottom-left'].includes(position)) {
    config.position = position;
  }

  const marginBottom = script.dataset['marginBottom'];
  if (marginBottom) {
    const val = parseInt(marginBottom, 10);
    if (!isNaN(val) && val >= 0 && val <= 200) config.marginBottom = val;
  }

  const marginSide = script.dataset['marginSide'];
  if (marginSide) {
    const val = parseInt(marginSide, 10);
    if (!isNaN(val) && val >= 0 && val <= 200) config.marginSide = val;
  }

  const apiUrl = script.dataset['apiUrl'];
  if (apiUrl) {
    config.apiUrl = apiUrl;
  }

  const greeting = script.dataset['greeting'];
  if (greeting) {
    config.greeting = greeting;
  }

  const placeholder = script.dataset['placeholder'];
  if (placeholder) {
    config.placeholder = placeholder;
  }

  // Parse sample questions (JSON array or comma-separated)
  const sampleQuestions = script.dataset['sampleQuestions'];
  if (sampleQuestions) {
    try {
      config.sampleQuestions = JSON.parse(sampleQuestions);
    } catch {
      // If not valid JSON, try comma-separated
      config.sampleQuestions = sampleQuestions.split(',').map(q => q.trim()).filter(q => q);
    }
  }

  // Parse theme from data attributes
  const theme: WidgetTheme = {};

  // Theme preset (green, blue, gold, red, teal, orange)
  const themePreset = script.dataset['theme'];
  if (themePreset) theme.preset = themePreset as WidgetTheme['preset'];

  // Custom accent colors
  const accentPrimary = script.dataset['accentPrimary'];
  if (accentPrimary) theme.accentPrimary = accentPrimary;

  const accentSecondary = script.dataset['accentSecondary'];
  if (accentSecondary) theme.accentSecondary = accentSecondary;

  // Legacy primaryColor support
  const primaryColor = script.dataset['primaryColor'];
  if (primaryColor) theme.primaryColor = primaryColor;

  const fontFamily = script.dataset['fontFamily'];
  if (fontFamily) theme.fontFamily = fontFamily;

  const borderRadius = script.dataset['borderRadius'];
  if (borderRadius) theme.borderRadius = borderRadius;

  const panelHeight = script.dataset['panelHeight'];
  if (panelHeight) theme.panelHeight = panelHeight;

  if (Object.keys(theme).length > 0) {
    config.theme = theme;
  }

  // Parse split panel mode
  const splitPanel = script.dataset['splitPanel'];
  if (splitPanel === 'true') {
    config.splitPanel = { enabled: true };
  }

  // Parse initial height for split panel (percentage of viewport)
  const initialHeight = script.dataset['initialHeight'];
  if (initialHeight) {
    const height = parseInt(initialHeight, 10);
    if (!isNaN(height) && height > 0 && height <= 100) {
      if (!config.splitPanel) {
        config.splitPanel = { enabled: true };
      }
      config.splitPanel.initialHeight = height;
    }
  }

  // Parse auto-open
  const autoOpen = script.dataset['autoOpen'];
  if (autoOpen === 'true' || autoOpen === '1') {
    config.autoOpen = true;
  } else if (autoOpen === 'false' || autoOpen === '0') {
    config.autoOpen = false;
  }

  // Parse visibility (visible or hidden)
  const visibility = script.dataset['visibility'];
  if (visibility && (visibility === 'visible' || visibility === 'hidden')) {
    config.visibility = visibility;
  }

  // Parse page observation flag
  const pageObservation = script.dataset['pageObservation'];
  if (pageObservation === 'true' || pageObservation === '1') {
    config.pageObservationEnabled = true;
  } else if (pageObservation === 'false' || pageObservation === '0') {
    config.pageObservationEnabled = false;
  }

  // Parse push-content (default: true — only explicit false disables)
  const pushContent = script.dataset['pushContent'];
  if (pushContent === 'false' || pushContent === '0') {
    config.pushContent = false;
  }

  // Parse mode (dark / light)
  const mode = script.dataset['mode'] as 'dark' | 'light' | undefined;
  if (mode && (mode === 'dark' || mode === 'light')) {
    config.mode = mode;
  }

  // Parse vertical / industry
  const vertical = script.dataset['vertical'];
  if (vertical) {
    config.vertical = vertical;
  }

  // Parse language
  const language = script.dataset['language'];
  if (language) {
    config.language = language;
  }

  // Parse voice config
  const voiceInput = script.dataset['voiceInput'];
  const voiceOutput = script.dataset['voiceOutput'];
  const voiceAutoSpeak = script.dataset['voiceAutoSpeak'];
  if (voiceInput || voiceOutput || voiceAutoSpeak) {
    config.voice = {
      enabled: true,
      inputEnabled: voiceInput === 'true',
      outputEnabled: voiceOutput === 'true',
      autoSpeak: voiceAutoSpeak === 'true',
    };
  }

  return config;
}

/**
 * Default localStorage keys to check for authentication tokens.
 */
const DEFAULT_AUTH_TOKEN_KEYS = [
  'accessToken',
  'authToken',
  'token',
  'jwt',
  'pms_billing_token',
  'auth_token',
  'access_token',
];

/**
 * Check if user is authenticated by looking for tokens in localStorage.
 *
 * @param authTokenKeys Custom key(s) to check, or uses defaults
 * @returns true if any token key has a value in localStorage
 */
export function isAuthenticatedFromStorage(
  authTokenKeys?: string | string[]
): boolean {
  try {
    const keys = authTokenKeys
      ? (Array.isArray(authTokenKeys) ? authTokenKeys : [authTokenKeys])
      : DEFAULT_AUTH_TOKEN_KEYS;

    return keys.some((key) => {
      const value = localStorage.getItem(key);
      return value !== null && value !== '';
    });
  } catch {
    // localStorage not available (SSR, iframe sandbox, etc.)
    return false;
  }
}

/**
 * Filter sample questions based on authentication state.
 * Auto-detects auth from localStorage if not explicitly provided.
 *
 * @param questions The raw sample questions (strings or objects with visibility rules)
 * @param isAuthenticated Whether the user is currently authenticated (explicit override)
 * @param authTokenKeys localStorage key(s) to check for auth tokens
 * @returns Array of question text strings, filtered by auth state
 */
export function filterSampleQuestions(
  questions: SampleQuestion[] | undefined,
  isAuthenticated?: boolean,
  authTokenKeys?: string | string[]
): string[] {
  if (!questions) return [];

  // Auto-detect auth state if not explicitly set
  const authenticated = isAuthenticated ?? isAuthenticatedFromStorage(authTokenKeys);

  return questions
    .filter((q) => {
      // Simple string - always show
      if (typeof q === 'string') return true;

      // Object with visibility rules
      if (q.onboardingOnly && authenticated) {
        // Hide onboarding questions for authenticated users
        return false;
      }
      if (q.authenticatedOnly && !authenticated) {
        // Hide authenticated-only questions for guests
        return false;
      }

      return true;
    })
    .map((q) => (typeof q === 'string' ? q : q.text));
}
